/**
 * Invoice PTC — layout factura comercial (sin bordes de grilla, sin marca de página).
 * Líneas de mercancía desde datos_formulario.lineasMercancia (captura packing list + PI).
 */
import jsPDF from 'jspdf';
import autoTable, { __createTable } from 'jspdf-autotable';
import ptcImage from '../../imagenes/ptc.jpg';
import firmaPtcImage from '../../imagenes/firma-ptc.png';
import {
  applyPtcFormUnitPrices,
  buildInvoicePtcMerchandise,
  normalizeGeneralMerchandiseDescription,
  packingListTotals,
  sortPtcMerchandiseLines,
  stripProductNameIfCoveredByTextoArriba,
  usdAmountInWords,
} from './invoicePtcMerchandise';
import {
  pairFromInvoiceDatos,
  textosAlternativosPtcFromPair,
} from './invoicePtcTextosMercaderia';
import { invoiceNumberCorrelativo } from './invoiceNumberUtils';
import {
  invoiceIssueDateText,
  resolveInvoiceIssueDate,
  resolveInvoiceIssueDateFromForm,
} from './invoiceIssueDate';
import { buildMerchandiseTableBody, drawMerchBodyVerticalOverlap, merchRowKind } from './invoicePtcMerchandiseTable';
import { contractNoFromProformas, proformaContractLabel } from './invoiceProformaUtils';
import {
  formatQtyWithVolumeUnit,
  normalizeVolumeUnitPtc,
  totalVolumeSummaryText,
} from './invoiceVolumeUnit';
import { totalInvoiceLabel } from './invoiceCurrencyLabel';
import { appendBankInformationPage } from '../documentos/bankInformationPage';

export { contractNoFromProformas } from './invoiceProformaUtils';

/** TOTAL DUE en letras, usando la moneda seleccionada en la invoice. */
function totalDueWordsPtc(amount, currency = 'USD') {
  const n = Math.round(Number(amount) * 100) / 100;
  if (!Number.isFinite(n) || n <= 0) return '';
  const whole = Math.floor(n);
  const cents = Math.round((n - whole) * 100);
  const words = usdAmountInWords(whole)
    .replace(/\s+DOLLARS?$/i, '')
    .trim();
  const suffix = String(currency).toUpperCase() === 'EUR' ? 'EUROS' : 'USD DOLLARS';
  return `${words}, ${String(cents).padStart(2, '0')}/100 ${suffix}`;
}

const RUT_EMPRESA = '77.710.540-K';
const SII_OFICINA = 'S.I.I. SANTIAGO ORIENTE';
const COMPANY_LINES = [
  'PALM TRADE CHILE SPA',
  'Avenida Presidente Kennedy 5146, oficina 51',
  'Vitacura, Santiago, Chile',
  'Fono: (56-2) 22190119 - Fax: (56-2) 22184390',
];

const tableGrid = {
  theme: 'grid',
  styles: {
    fontSize: 7,
    cellPadding: { top: 1.2, right: 1.2, bottom: 1.2, left: 1.2 },
    lineColor: [0, 0, 0],
    lineWidth: 0.15,
    textColor: [0, 0, 0],
    valign: 'middle',
    halign: 'center',
  },
  headStyles: {
    fontSize: 7,
    fontStyle: 'bold',
    fillColor: [255, 255, 255],
    textColor: [0, 0, 0],
    halign: 'center',
    valign: 'middle',
  },
};

/** Proporciones fila embarque (Vessel…): 35 / 25 / 20 / 20 */
const col4Vessel = (w) => ({
  0: { cellWidth: w * 0.35 },
  1: { cellWidth: w * 0.25 },
  2: { cellWidth: w * 0.2 },
  3: { cellWidth: w * 0.2 },
});

/** Proporciones fila comercial + peso: 23 / 29 / 28 / 10 / 10 (últimos 20 % = Total Weight) */
const col5SalesWeight = (w) => ({
  0: { cellWidth: w * 0.23 },
  1: { cellWidth: w * 0.29 },
  2: { cellWidth: w * 0.28 },
  3: { cellWidth: w * 0.1 },
  4: { cellWidth: w * 0.1 },
});

/** Mercancía: Internal 12 / Qty 12 / Desc 35 / N° 5 / Date 8 / Curr 8 / Unit 10 / Total 10 */
const col8Merchandise = (w) => ({
  0: { cellWidth: w * 0.12 },
  1: { cellWidth: w * 0.12 },
  2: { cellWidth: w * 0.35, halign: 'left' },
  3: { cellWidth: w * 0.05 },
  4: { cellWidth: w * 0.08 },
  5: { cellWidth: w * 0.08 },
  6: { cellWidth: w * 0.1, halign: 'right' },
  7: { cellWidth: w * 0.1, halign: 'right' },
});

function priceCellPdf(v) {
  if (v == null || v === '' || Number.isNaN(Number(v))) return '';
  return fmtNum(Number(v), 2);
}

const merchHeadLabel = { fontStyle: 'bold', halign: 'center', valign: 'middle' };
const merchHeadCaps = { ...merchHeadLabel, fontStyle: 'bold' };
/** Celda agrupada DJC: sin divisiones internas en la fila superior. */
const merchHeadDjcGroup = {
  ...merchHeadCaps,
  lineWidth: { top: 0.15, right: 0.15, bottom: 0.15, left: 0.15 },
};

function safe(s) {
  return s == null ? '' : String(s);
}

function upper(s) {
  const t = safe(s).trim();
  return t ? t.toUpperCase() : '';
}

function loadingPortForPdf(port) {
  return safe(port)
    .replace(/\s*\((?:BRASIL|BRAZIL)\)\s*/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function unloadingPortForPdf(port, country) {
  const cleanPort = safe(port)
    .replace(/\s*\([^)]*\)\s*/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const cleanCountry = safe(country).replace(/\s+/g, ' ').trim();
  if (!cleanPort) return cleanCountry;
  return cleanCountry ? `${cleanPort}, ${cleanCountry}` : cleanPort;
}

function fmtNum(v, dec = 2) {
  if (v == null || v === '' || Number.isNaN(Number(v))) return '';
  return new Intl.NumberFormat('es-CL', {
    minimumFractionDigits: dec,
    maximumFractionDigits: dec,
  }).format(Number(v));
}

function ajustesPtcResumen(ajustes) {
  return (Array.isArray(ajustes) ? ajustes : [])
    .map((ajuste) => {
      const valor = Number(ajuste?.valor);
      if (!Number.isFinite(valor) || valor === 0 || !String(ajuste?.descripcion || '').trim()) return null;
      const descuento = ajuste.tipo === 'descuento';
      return {
        descripcion: String(ajuste.descripcion).trim(),
        tipo: descuento ? 'Descuento' : 'Cargo',
        valor: descuento ? -Math.abs(valor) : Math.abs(valor),
      };
    })
    .filter(Boolean);
}

function totalAjustesPtc(ajustes) {
  return ajustes.reduce((sum, ajuste) => sum + ajuste.valor, 0);
}

function totalComposicionPtc(f) {
  return [f?.valorFobPtc, f?.valorFletePtc, f?.valorSeguroPtc].reduce(
    (sum, value) => sum + (value != null && value !== '' && Number.isFinite(Number(value)) ? Number(value) : 0),
    0
  );
}

function fmtDateLongEn(isoDate) {
  if (!isoDate) return '';
  const d = new Date(isoDate + (isoDate.length === 10 ? 'T12:00:00' : ''));
  if (Number.isNaN(d.getTime())) return safe(isoDate);
  return d.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' });
}

function fmtDateDjc(isoDate) {
  if (!isoDate) return '';
  const d = new Date(isoDate + (isoDate.length === 10 ? 'T12:00:00' : ''));
  if (Number.isNaN(d.getTime())) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}-${mm}-${d.getFullYear()}`;
}

function parseDireccionCliente(texto, pinCode) {
  const lines = safe(texto)
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  const sinPin = lines.filter((l) => !/^PIN CODE\s*:/i.test(l));
  return {
    address: sinPin[0] || '',
    extra: sinPin.slice(1).join(', '),
    postal: pinCode || '',
  };
}

/** País solo en City/Country: quita líneas de Address que lo repiten (snapshots antiguos). */
function addressLinesWithoutCountry(texto, paisDestino) {
  const country = upper(paisDestino);
  return safe(texto)
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean)
    .filter((l) => !/^PIN CODE\s*:/i.test(l))
    .filter((l) => !country || upper(l) !== country)
    .map((l) => upper(l));
}

function descriptionForPdf(ln) {
  const raw = safe(ln.descriptionBlock || ln.productName).trim();
  return raw
    .split(/\r?\n/)
    .map((line) => upper(line))
    .filter(Boolean)
    .join('\n');
}

export function mapLineasMercanciaToPdfRows(lineasMercancia, inv, f) {
  const numero = invoiceNumberCorrelativo(
    inv?.numero_invoice || f?.numeroIdentificadorPtc || ''
  );
  const fecha = f?.fechaEmbarque || inv?.fecha_embarque || inv?.fecha_invoice || f?.fechaInvoice || '';
  const currency = safe(f?.invoiceCurrency || 'USD').toUpperCase() || 'USD';
  const volumeUnit = normalizeVolumeUnitPtc(f?.unidadVolumenPtc);
  const textoArriba = pairFromInvoiceDatos(f).arriba;
  const lineasConDescripcionUnica = stripProductNameIfCoveredByTextoArriba(
    normalizeGeneralMerchandiseDescription(
      sortPtcMerchandiseLines(lineasMercancia),
      f?.productDescriptionSnapshot
    ),
    { textoArriba, productDescriptionSnapshot: f?.productDescriptionSnapshot }
  );
  return lineasConDescripcionUnica.map((ln) => {
    const qty =
      ln.quantityM3 != null && !Number.isNaN(Number(ln.quantityM3))
        ? Number(ln.quantityM3)
        : null;
    const unit =
      ln.unitPriceUsd != null && !Number.isNaN(Number(ln.unitPriceUsd))
        ? Number(ln.unitPriceUsd)
        : null;
    let total =
      ln.totalValueUsd != null && !Number.isNaN(Number(ln.totalValueUsd))
        ? Number(ln.totalValueUsd)
        : null;
    if (total == null && qty != null && unit != null) {
      total = Math.round(qty * unit * 100) / 100; // M³ × USD/m³ (PI), máx. 2 dec.
    }
    return {
      internalCode: '',
      quantity: qty != null ? formatQtyWithVolumeUnit(qty, volumeUnit, fmtNum) : '',
      description: descriptionForPdf(ln),
      productName: ln.productName,
      djcNum: invoiceNumberCorrelativo(ln.djcNum) || numero,
      djcDate: fmtDateDjc(ln.djcDate || fecha),
      currency: safe(ln.currency || currency).toUpperCase() || 'USD',
      unitPrice: unit,
      totalValue: total,
    };
  });
}

function lineasFromFormulario(f, inv) {
  const saved = f?.lineasMercancia;
  if (Array.isArray(saved) && saved.length) {
    return mapLineasMercanciaToPdfRows(saved, inv, f);
  }
  return [];
}

/** Reconstruye líneas de mercancía desde packing list + PIs vinculadas. */
export async function lineasMercanciaFromPackingList(pl, proforma, inv, f, opts = {}) {
  const calidades = f?.calidades || [];
  const idProformas = opts.idProformas || resolveProformaIds(pl, f);
  const proformasLista =
    opts.proformasLista?.length > 0
      ? opts.proformasLista
      : proforma
        ? [proforma]
        : [];
  const merc = buildInvoicePtcMerchandise(pl?.lineas || [], proforma, {
    currency: f?.invoiceCurrency || 'USD',
    fechaInvoice: f?.fechaEmbarque || inv?.fecha_embarque || inv?.fecha_invoice || f?.fechaInvoice,
    fechaEmbarque: pl?.fecha_embarque || f?.fechaEmbarque,
    numeroInvoice: inv?.numero_invoice || f?.numeroIdentificadorPtc || '',
    productDescriptionSnapshot:
      pl?.product_description_snapshot || f?.productDescriptionSnapshot || '',
    calidades,
    clientes: opts.clientes || f?.clientes || [],
    proformasLista,
    idProformas,
  });
  return {
    lineas: mapLineasMercanciaToPdfRows(
      applyPtcFormUnitPrices(merc.lineasMercancia, f?.lineasMercancia),
      inv,
      f
    ),
    totalBundles: merc.totalBundles,
    totalM3: merc.totalM3,
    totalUsd: merc.totalUsd,
  };
}

function piLabelUpper(pi, clientes = []) {
  if (!pi) return '';
  return proformaContractLabel(pi, clientes).trim().toUpperCase();
}

export function purchaseOrderFromProformas(proformas, idProformas) {
  const lista = proformas || [];
  const seen = new Set();
  const pos = [];
  (idProformas || []).forEach((id) => {
    const pi = lista.find((p) => String(p.id_proforma) === String(id));
    const po = safe(pi?.po).trim();
    if (!po) return;
    const key = po.toUpperCase();
    if (seen.has(key)) return;
    seen.add(key);
    pos.push(po);
  });
  return pos.join(', ');
}

export async function fetchPurchaseOrderFromProformas(
  apiBase,
  headers,
  idProformas,
  proformasEnMemoria = []
) {
  const ids = idProformas || [];
  if (!ids.length) return '';
  const memoria = Array.isArray(proformasEnMemoria)
    ? proformasEnMemoria
    : proformasEnMemoria
      ? [proformasEnMemoria]
      : [];
  const tieneTodas = ids.every((id) =>
    memoria.some((p) => String(p.id_proforma) === String(id))
  );
  if (tieneTodas && memoria.length) {
    return purchaseOrderFromProformas(memoria, ids);
  }
  if (!apiBase || !headers) {
    return purchaseOrderFromProformas(memoria, ids);
  }
  const res = await fetch(`${apiBase}/proformas`, { headers });
  if (!res.ok) return purchaseOrderFromProformas(memoria, ids);
  const all = await res.json();
  return purchaseOrderFromProformas(all, ids);
}

export function resolvePurchaseOrderPtc(f, proformasLista, idProformas) {
  const saved = safe(f?.purchaseOrder).trim();
  if (saved) return saved;
  return purchaseOrderFromProformas(
    proformasLista,
    idProformas || f?.idsProformaSeleccion || []
  );
}

/** PI del listado cargado en pantalla (Contract No. / ids seleccionados). */
export function proformaFromLista(proformas, idProformas, contractNo) {
  const lista = proformas || [];
  const ids = idProformas || [];
  if (ids.length) {
    const lastId = ids[ids.length - 1];
    const found = lista.find((p) => String(p.id_proforma) === String(lastId));
    if (found) return found;
  }
  const c = safe(contractNo).trim().toUpperCase();
  if (!c) return null;
  return (
    lista.find((p) => piLabelUpper(p) === c) ||
    lista.find((p) => `PI #${p.id_proforma}`.toUpperCase() === c) ||
    null
  );
}

export function lineasPdfTienenPrecios(lineas) {
  return (lineas || []).some((ln) => {
    const p = ln.unitPrice ?? ln.unitPriceUsd;
    return p != null && p !== '' && !Number.isNaN(Number(p));
  });
}

/** IDs de PI: packing list, o Contract No. guardado en el formulario. */
export function resolveProformaIds(pl, datosFormulario) {
  const fromPl = pl?.id_proformas;
  if (Array.isArray(fromPl) && fromPl.length) return fromPl;
  const fromForm = datosFormulario?.idsProformaSeleccion;
  if (Array.isArray(fromForm) && fromForm.length) return fromForm;
  return [];
}

export async function fetchOrdenCompraById(apiBase, headers, idOrdenCompra) {
  if (!idOrdenCompra || !apiBase || !headers) return null;
  const res = await fetch(`${apiBase}/ordenescompra`, { headers });
  if (!res.ok) return null;
  const all = await res.json();
  return (
    all.find((o) => String(o.id_ordencompra) === String(idOrdenCompra)) || null
  );
}

export async function fetchProformaUltima(apiBase, headers, idProformas, proformaEnMemoria = null) {
  const lista = await fetchProformasForIds(apiBase, headers, idProformas, proformaEnMemoria);
  return lista.length ? lista[lista.length - 1] : proformaEnMemoria || null;
}

/** Todas las PIs del packing list / invoice, en orden. */
export async function fetchProformasForIds(
  apiBase,
  headers,
  idProformas,
  proformasEnMemoria = []
) {
  const ids = (idProformas || []).map((id) => String(id));
  if (!ids.length) return [];
  const memoria = Array.isArray(proformasEnMemoria)
    ? proformasEnMemoria
    : proformasEnMemoria
      ? [proformasEnMemoria]
      : [];
  const resolved = ids.map(
    (id) =>
      memoria.find((p) => String(p.id_proforma) === id) ||
      null
  );
  if (resolved.every(Boolean)) return resolved;
  if (!apiBase || !headers) return resolved.filter(Boolean);
  const res = await fetch(`${apiBase}/proformas`, { headers });
  if (!res.ok) return resolved.filter(Boolean);
  const all = await res.json();
  return ids
    .map(
      (id) =>
        all.find((p) => String(p.id_proforma) === id) ||
        memoria.find((p) => String(p.id_proforma) === id) ||
        null
    )
    .filter(Boolean);
}

/**
 * Params PDF desde detalle (packing list) + PI ya cargada en pantalla (Contract No.).
 * Evita depender solo del API cuando la PI está en el formulario.
 */
export function invoicePtcParamsFromFormAndDetail(
  datosFormulario,
  plLineas,
  proforma,
  overrides = {}
) {
  const f = datosFormulario || {};
  const calidades = overrides.calidades || f.calidades || [];
  const clientes = overrides.clientes || f.clientes || [];
  const inv = {
    numero_invoice: overrides.numeroInvoice ?? f.numeroIdentificadorPtc ?? '',
    fecha_invoice: resolveInvoiceIssueDateFromForm(f),
    datos_formulario: f,
  };
  const merc = buildInvoicePtcMerchandise(plLineas || [], proforma, {
    currency: f.invoiceCurrency || 'USD',
    fechaInvoice: resolveInvoiceIssueDateFromForm(f),
    fechaEmbarque: f.fechaEmbarque,
    numeroInvoice: inv.numero_invoice,
    productDescriptionSnapshot: f.productDescriptionSnapshot || '',
    calidades,
    clientes,
    proformasLista: overrides.proformasLista || [],
    idProformas: f?.idsProformaSeleccion || overrides.idProformas || [],
  });
  const lineasPdf = mapLineasMercanciaToPdfRows(
    applyPtcFormUnitPrices(merc.lineasMercancia, f.lineasMercancia),
    inv,
    f
  );
  const base = invoicePtcParamsFromForm(datosFormulario, overrides);
  const tienePrecios = lineasPdfTienenPrecios(lineasPdf);
  const valorUsd = base.valorUsd;
  const proformasLista = overrides.proformasLista || [];
  const purchaseOrder = resolvePurchaseOrderPtc(
    f,
    proformasLista,
    f?.idsProformaSeleccion
  );
  const contractNo =
    contractNoFromProformas(proformasLista, f?.idsProformaSeleccion, clientes) ||
    f?.contractNo ||
    base.contractNo;

  return {
    ...base,
    contractNo,
    lineas: tienePrecios ? lineasPdf : base.lineas,
    valorUsd,
    totalBundles: merc.totalBundles ?? f.totalBundles ?? base.totalBundles,
    totalM3: merc.totalM3 ?? f.totalM3 ?? base.totalM3,
    purchaseOrder: purchaseOrder || base.purchaseOrder,
  };
}

/**
 * Params PDF PTC: datos guardados + mercancía recalculada desde packing list asociado.
 */
export async function invoicePtcParamsFromApiWithPackingList(
  inv,
  { apiBase, headers, proformaEnMemoria = null }
) {
  const base = invoicePtcParamsFromApi(inv);
  const idPl = inv?.id_packing_list;
  const f = inv?.datos_formulario || {};
  if (!idPl || !apiBase || !headers) return base;

  try {
    const plRes = await fetch(`${apiBase}/packinglists/${idPl}`, { headers });
    if (!plRes.ok) return base;
    const pl = await plRes.json();
    const idProformas = resolveProformaIds(pl, f);
    const proformasLista = await fetchProformasForIds(
      apiBase,
      headers,
      idProformas,
      proformaEnMemoria
    );
    let proforma = proformasLista[proformasLista.length - 1] || proformaEnMemoria || null;
    if (!proforma && pl.id_ordencompra) {
      proforma = await fetchOrdenCompraById(apiBase, headers, pl.id_ordencompra);
      if (proforma) proformasLista.push(proforma);
    }
    const merc = await lineasMercanciaFromPackingList(pl, proforma, inv, f, {
      proformasLista,
      idProformas,
    });
    const plTotals = packingListTotals(pl.lineas || []);

    const lineasApi = merc.lineas;
    const lineasConPrecio = lineasApi.filter((ln) => lineasPdfTienenPrecios([ln]));
    const lineasForm = base.lineas.filter((ln) => lineasPdfTienenPrecios([ln]));
    const lineas =
      lineasConPrecio.length > 0
        ? lineasApi
        : lineasForm.length > 0
          ? lineasForm
          : lineasApi.length
            ? lineasApi
            : base.lineas;

    const totalUsd = base.valorUsd;
    const poFromPi = await fetchPurchaseOrderFromProformas(
      apiBase,
      headers,
      idProformas,
      proforma
    );
    const purchaseOrder = resolvePurchaseOrderPtc(f, [], idProformas) || poFromPi;
    const contractNo =
      contractNoFromProformas(proformasLista, idProformas) ||
      f?.contractNo ||
      inv?.contrato_pi ||
      base.contractNo;

    return {
      ...base,
      contractNo,
      lineas,
      totalBundles: merc.totalBundles ?? plTotals.totalBundles ?? base.totalBundles,
      totalM3: merc.totalM3 ?? plTotals.totalM3 ?? base.totalM3,
      valorUsd: totalUsd,
      purchaseOrder: purchaseOrder || base.purchaseOrder,
      fechaInvoice: resolveInvoiceIssueDate(inv, pl),
      nombreBl: base.nombreBl || pl.nombre_bl || '',
      nombreNave: base.nombreNave || pl.nombre_nave || '',
      paisOrigen: base.paisOrigen || pl.pais_origen || '',
      paisDestino: base.paisDestino || pl.pais_destino || '',
      puertoOrigen: base.puertoOrigen || pl.nombre_puerto_origen || '',
      puertoDestino: base.puertoDestino || pl.nombre_puerto_destino || '',
      lugarEntrega: base.lugarEntrega || pl.lugar_entrega || '',
    };
  } catch (err) {
    console.warn('invoicePtcParamsFromApiWithPackingList:', err);
    return base;
  }
}

/**
 * Vista previa / crear invoice: mismos datos de mercancía que al descargar (packing list + PI).
 */
export async function invoicePtcParamsFromFormWithPackingList(
  datosFormulario,
  overrides = {},
  { apiBase, headers, idPackingList, proformaEnMemoria = null, plLineas = null }
) {
  const f = datosFormulario || {};
  const inv = {
    id_packing_list: idPackingList,
    numero_invoice: overrides.numeroInvoice ?? f.numeroIdentificadorPtc ?? '',
    fecha_invoice: resolveInvoiceIssueDateFromForm(f),
    nombre_cliente: f.clienteNombre,
    nombre_bl: f.nombreBl,
    pais_destino: f.paisDestino,
    valor_usd: null,
    contrato_pi: f.contractNo,
    total_m3: f.totalM3,
    datos_formulario: f,
  };
  const sync = invoicePtcParamsFromFormAndDetail(
    f,
    plLineas,
    proformaEnMemoria,
    overrides
  );
  if (lineasPdfTienenPrecios(sync.lineas)) return sync;
  return invoicePtcParamsFromApiWithPackingList(inv, {
    apiBase,
    headers,
    proformaEnMemoria,
  });
}

/** Datos del formulario crear invoice → mismos params que al descargar. */
export function invoicePtcParamsFromForm(datosFormulario, overrides = {}) {
  const f = datosFormulario || {};
  const valorUsd =
    overrides.valorUsd != null
      ? overrides.valorUsd
      : f.totalUsd != null
        ? Number(f.totalUsd)
        : null;
  return invoicePtcParamsFromApi({
    numero_invoice: overrides.numeroInvoice ?? f.numeroIdentificadorPtc ?? '',
    fecha_invoice: resolveInvoiceIssueDateFromForm(f),
    nombre_cliente: f.clienteNombre,
    nombre_bl: f.nombreBl,
    pais_destino: f.paisDestino,
    valor_usd: valorUsd,
    contrato_pi: f.contractNo,
    total_m3: f.totalM3,
    datos_formulario: f,
  });
}

/** Respuesta GET /commercialinvoices/:id → params para un único PDF PTC. */
export function invoicePtcParamsFromApi(inv, proformasLista = []) {
  const f = inv?.datos_formulario || {};
  const contractNoResolved =
    contractNoFromProformas(proformasLista, f?.idsProformaSeleccion) ||
    inv?.contrato_pi ||
    f?.contractNo ||
    '';
  const lineas = lineasFromFormulario(f, inv);
  const sumProducts =
    lineas.reduce((s, ln) => s + (Number(ln.totalValue) || 0), 0) || null;
  const valorMercaderiaUsd =
    f?.totalUsd != null && f.totalUsd !== ''
      ? Number(f.totalUsd)
      : inv?.valor_usd != null
        ? Number(inv.valor_usd)
        : sumProducts;
  const ajustesPtc = ajustesPtcResumen(f.ajustesPtc);
  const composicionPtc = totalComposicionPtc(f);
  const valorFinalCalculado = valorMercaderiaUsd != null
    ? Math.round((valorMercaderiaUsd + totalAjustesPtc(ajustesPtc) + composicionPtc) * 100) / 100
    : null;
  const totalUsd =
    f?.valorFinalUsd != null && f.valorFinalUsd !== ''
      ? Number(f.valorFinalUsd)
      : inv?.valor_usd != null && ajustesPtc.length
        ? Number(inv.valor_usd)
        : valorFinalCalculado;

  return {
    numeroInvoice: inv?.numero_invoice || f?.numeroIdentificadorPtc || '',
    fechaInvoice: resolveInvoiceIssueDate(inv),
    issueCity: 'Santiago',
    clienteNombre: inv?.nombre_cliente || f?.clienteNombre || '',
    clienteDireccion: f?.clienteDireccion || '',
    taxId: f?.taxId || '',
    pinCode: f?.pinCode || '',
    paisOrigen: f?.paisOrigen || '',
    paisDestino: inv?.pais_destino || f?.paisDestino || '',
    puertoOrigen: f?.puertoOrigen || '',
    puertoDestino: f?.puertoDestino || '',
    lugarEntrega: f?.lugarEntrega || '',
    nombreNave: f?.nombreNave || '',
    nombreBl: inv?.nombre_bl || f?.nombreBl || '',
    contractNo: contractNoResolved,
    purchaseOrder: f?.purchaseOrder || '',
    salesTerms: f?.salesTerms || 'AFIRME',
    incoterms: f?.incoterms || '',
    paymentTerms: f?.paymentTermsPtc || '',
    netWeight: f?.netWeight,
    grossWeight: f?.grossWeight,
    valorUsd: totalUsd,
    valorMercaderiaUsd,
    ajustesPtc,
    valorFinalUsd: totalUsd,
    valorFobPtc: f?.valorFobPtc,
    valorFletePtc: f?.valorFletePtc,
    valorSeguroPtc: f?.valorSeguroPtc,
    currency: f?.invoiceCurrency || 'USD',
    totalBundles: f?.totalBundles,
    totalM3: f?.totalM3 ?? inv?.total_m3,
    unidadVolumenPtc: normalizeVolumeUnitPtc(f?.unidadVolumenPtc),
    lineas,
    textosAlternativosPtc: textosAlternativosPtcFromPair(pairFromInvoiceDatos(f)),
  };
}

/** Bloque cliente izquierdo con interlineado compacto (sin autoTable). */
function drawLeftCustomerBlock(doc, opts) {
  const {
    startX,
    startY,
    blockW,
    labelW,
    nombre,
    addrLines,
    cityCountry,
    postal,
  } = opts;
  const padX = 1.5;
  const padY = 1.5;
  const fontSize = 8;
  const lineH = 2.8;
  const labelX = startX + padX;
  const valueX = startX + labelW + padX * 0.5;
  const valueW = blockW - labelW - padX * 2.5;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(fontSize);

  let cy = startY + padY + 3;

  const row = (label, value) => {
    doc.text(label, labelX, cy);
    doc.text(value || '', valueX, cy);
    cy += lineH;
  };

  const nombreParts = doc.splitTextToSize(nombre || '', valueW);
  if (nombreParts.length) {
    nombreParts.forEach((part, index) => {
      if (index === 0) doc.text('Mr./Mrs.:', labelX, cy);
      doc.text(part, valueX, cy);
      cy += lineH;
    });
  } else {
    doc.text('Mr./Mrs.:', labelX, cy);
    cy += lineH;
  }

  let addressStarted = false;
  const linesToDraw = addrLines.length ? addrLines : [''];
  linesToDraw.forEach((line) => {
    const wrapped = doc.splitTextToSize(line || '', valueW);
    wrapped.forEach((part) => {
      if (!addressStarted) {
        doc.text('Address:', labelX, cy);
        addressStarted = true;
      }
      doc.text(part, valueX, cy);
      cy += lineH;
    });
  });
  if (!addressStarted) {
    doc.text('Address:', labelX, cy);
    cy += lineH;
  }

  row('State:', '');
  row('City/Country:', cityCountry);
  row('Phone:', '');
  row('Fax:', '');
  row('Postal Code/Pin Code:', postal);

  return cy + padY;
}

const RIGHT_LOGISTICS_ROWS = 6;

/** Tabla origen/destino/puertos; altura total = blockHeight (misma que bloque cliente). */
function drawRightLogisticsBlock(doc, opts) {
  const { startY, x, width, params, blockHeight } = opts;
  const baseRowH = Math.floor((blockHeight / RIGHT_LOGISTICS_ROWS) * 100) / 100;
  const lastRowH = Math.round((blockHeight - baseRowH * (RIGHT_LOGISTICS_ROWS - 1)) * 100) / 100;

  autoTable(doc, {
    startY,
    margin: { left: x, right: doc.internal.pageSize.getWidth() - x - width },
    tableWidth: width,
    theme: 'grid',
    styles: {
      fontSize: 8,
      cellPadding: { top: 1.2, right: 1.2, bottom: 1.2, left: 1.2 },
      lineColor: [0, 0, 0],
      lineWidth: 0.15,
      textColor: [0, 0, 0],
      valign: 'middle',
      halign: 'center',
    },
    didParseCell(data) {
      if (data.section === 'body') {
        const isLast = data.row.index === RIGHT_LOGISTICS_ROWS - 1;
        data.cell.height = isLast ? lastRowH : baseRowH;
        if (isLast && opts.closeBottomAt) {
          data.cell.styles.lineWidth = {
            top: 0.15,
            right: 0.15,
            bottom: 0,
            left: 0.15,
          };
        }
      }
    },
    body: [
      [
        { content: 'Country of Origin', styles: { fontSize: 7 } },
        { content: 'Destination Country', styles: { fontSize: 7 } },
      ],
      [
        { content: upper(params.paisOrigen) },
        { content: upper(params.paisDestino) },
      ],
      [
        { content: 'Loading Port', styles: { fontSize: 7 } },
        { content: 'Unloading Port', styles: { fontSize: 7 } },
      ],
      [
        { content: upper(loadingPortForPdf(params.puertoOrigen)) },
        { content: upper(unloadingPortForPdf(params.puertoDestino, params.paisDestino)) },
      ],
      [
        { content: 'Place of delivery', colSpan: 2, styles: { fontSize: 7 } },
      ],
      [
        {
          content: upper(params.lugarEntrega || params.puertoDestino),
          colSpan: 2,
        },
      ],
    ],
    columnStyles: {
      0: { cellWidth: width / 2 },
      1: { cellWidth: width / 2 },
    },
  });
  const finalY = doc.lastAutoTable?.finalY ?? startY + blockHeight;
  return {
    finalY,
    left: x,
    right: x + width,
  };
}

/** Cierra borde inferior y esquina inferior derecha de la tabla logística. */
function sealRightLogisticsBottom(doc, tableBox, blockEndY) {
  const { left, right, finalY } = tableBox;
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.15);
  doc.line(left, blockEndY, right, blockEndY);
  if (finalY < blockEndY - 0.05) {
    doc.line(right, finalY, right, blockEndY);
  }
}

/** Altura reservada al pie (firma fija + margen de tablas). A4 mm. */
const PTC_SIGNATURE_BOTTOM_PAD = 10;
const PTC_SIGNATURE_BLOCK_H = 15 + 2 + 9;
const PTC_SIGNATURE_ZONE_H = PTC_SIGNATURE_BOTTOM_PAD + PTC_SIGNATURE_BLOCK_H;
/** Margen extra para no pegar el pie a la zona de firma. */
const PTC_FILLER_SAFETY_MM = 4;

function ptcSignatureZoneTop(doc) {
  return doc.internal.pageSize.getHeight() - PTC_SIGNATURE_ZONE_H;
}

/** Mide con el mismo motor, columnas y estilos usados para dibujar. */
function measurePtcTable(doc, options) {
  const table = __createTable(doc, options);
  return {
    rows: table.body.map((row) => row.height),
    head: table.getHeadHeight(table.columns),
    top: table.settings.margin.top,
  };
}

/** Reserva el cierre en la última hoja y lo acompaña con el último producto. */
function planPtcMerchPages(rows, heights, headH, startY, topY, bottomY, footerH) {
  const pages = [{ indices: [], startY, height: headH }];
  heights.forEach((height, index) => {
    let page = pages[pages.length - 1];
    if (page.indices.length && page.startY + page.height + height > bottomY) {
      page = { indices: [], startY: topY, height: headH };
      pages.push(page);
    }
    page.indices.push(index);
    page.height += height;
  });
  const last = pages[pages.length - 1];
  if (
    last.startY + last.height + footerH <= bottomY &&
    last.indices.some((index) => merchRowKind(rows[index]) === 'product')
  ) return pages;

  const lastProduct = rows.reduce(
    (lastIndex, row, index) => merchRowKind(row) === 'product' ? index : lastIndex,
    -1
  );
  if (lastProduct < 0) return pages;
  const tail = rows.map((_, index) => index).slice(lastProduct);
  const tailH = tail.reduce((sum, index) => sum + heights[index], headH);
  // Si un producto y sus textos ya exceden una hoja, conservar el salto nativo.
  if (topY + tailH + footerH > bottomY) return pages;
  for (const page of pages) {
    page.indices = page.indices.filter((index) => index < lastProduct);
    page.height = headH + page.indices.reduce((sum, index) => sum + heights[index], 0);
  }
  while (pages.length && !pages[pages.length - 1].indices.length) pages.pop();
  if (!pages.length && startY + tailH + footerH <= bottomY) {
    return [{ indices: tail, startY, height: tailH }];
  }
  if (!pages.length) pages.push({ indices: [], startY, height: headH });
  pages.push({ indices: tail, startY: topY, height: tailH });
  return pages;
}

const merchBodyVertLines = { top: 0, right: 0.15, bottom: 0, left: 0.15 };
const merchGridLine = { top: 0.15, right: 0.15, bottom: 0.15, left: 0.15 };
const merchLineColor = [0, 0, 0];

function applyMerchTableCellBorders(data, rowKind) {
  data.cell.styles.lineColor = merchLineColor;
  data.cell.styles.lineWidth = { ...merchBodyVertLines };
  if (rowKind === 'product' && data.row.index === 0) {
    data.cell.styles.lineWidth.top = 0.15;
  }
}

/** Fila vacía (8 celdas) para mantener líneas verticales en el espacio de relleno. */
function merchFillerRowCells(fillerH) {
  return Array.from({ length: 8 }, (_, col) => ({
    content: '',
    styles: {
      minCellHeight: col === 0 ? fillerH : undefined,
      valign: 'top',
      lineWidth: merchBodyVertLines,
    },
  }));
}

/** Posiciones X de bordes de columnas mercancía (9 puntos: izq + 8 columnas). */
function merchTableColumnXs(margin, contentW) {
  const cols = col8Merchandise(contentW);
  const xs = [margin];
  let x = margin;
  for (let i = 0; i < 8; i++) {
    x += cols[i].cellWidth;
    xs.push(x);
  }
  return xs;
}

/** Cierra una hoja intermedia: horizontales al corte y verticales solo bajo el encabezado. */
function drawPtcMerchPageClose(doc, margin, contentW, yBottom, headBottomY) {
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.15);
  const yTop = headBottomY != null && headBottomY < yBottom ? headBottomY : yBottom;
  if (yBottom - yTop > 0.5) {
    const xs = merchTableColumnXs(margin, contentW);
    for (const x of xs) {
      doc.line(x, yTop, x, yBottom);
    }
  }
  doc.line(margin, yBottom, margin + contentW, yBottom);
}

/**
 * Y donde debe comenzar el pie para quedar anclado sobre la zona de firma.
 */
function ptcMerchFooterStartY(doc, productsEndY, footerH) {
  const zoneTop = ptcSignatureZoneTop(doc);
  const anchored = zoneTop - footerH - PTC_FILLER_SAFETY_MM;
  if (anchored < productsEndY + 1) return productsEndY;
  return anchored;
}

/** Filas de pie (totales, ajustes, due) dentro de la misma tabla mercancía. */
function buildPtcMerchFooterRows({
  totalesIzq,
  totalUsdFmt,
  moneda,
  ajustes,
  composicion,
  totalUsdNum,
  dueFooterH,
}) {
  const grid = { lineWidth: merchGridLine, lineColor: merchLineColor, rowPageBreak: 'avoid' };
  const rows = [
    [
      {
        content: totalesIzq,
        colSpan: 6,
        styles: { fontStyle: 'bold', fontSize: 7, halign: 'left', ...grid },
      },
      { content: `Total ${moneda}`, styles: { fontStyle: 'bold', halign: 'right', ...grid } },
      { content: totalUsdFmt, styles: { fontStyle: 'bold', halign: 'right', ...grid } },
    ],
  ];

  for (const ajuste of ajustes) {
    rows.push([
      { content: ajuste.descripcion, colSpan: 6, styles: { halign: 'left', ...grid } },
      { content: ajuste.tipo, styles: { halign: 'right', ...grid } },
      {
        content: `${ajuste.valor < 0 ? '-' : '+'}${fmtNum(Math.abs(ajuste.valor), 2)} ${moneda}`,
        styles: { halign: 'right', ...grid },
      },
    ]);
  }

  if (composicion.length) {
    const composicionTotal = composicion.reduce((sum, [, value]) => sum + Number(value), 0);
    for (const [label, value] of composicion) {
      rows.push([
        { content: '', colSpan: 6, styles: grid },
        { content: label, styles: { halign: 'right', ...grid } },
        { content: `${fmtNum(value, 2)} ${moneda}`, styles: { halign: 'right', ...grid } },
      ]);
    }
    rows.push([
      { content: 'Valor total', colSpan: 6, styles: { fontStyle: 'bold', halign: 'left', ...grid } },
      { content: '', styles: { fontStyle: 'bold', ...grid } },
      {
        content: `${fmtNum(composicionTotal, 2)} ${moneda}`,
        styles: { fontStyle: 'bold', halign: 'right', ...grid },
      },
    ]);
  }

  if (totalUsdNum != null || ajustes.length || composicion.length) {
    rows.push([
      {
        content: totalInvoiceLabel(moneda),
        colSpan: 6,
        styles: { fontStyle: 'bold', halign: 'left', ...grid },
      },
      { content: '', styles: { fontStyle: 'bold', ...grid } },
      {
        content: totalUsdNum != null ? `${fmtNum(totalUsdNum, 2)} ${moneda}` : '',
        styles: { fontStyle: 'bold', halign: 'right', ...grid },
      },
    ]);
  }

  rows.push([
    {
      content: ' ',
      colSpan: 8,
      styles: {
        minCellHeight: dueFooterH,
        valign: 'top',
        rowPageBreak: 'avoid',
        ...grid,
      },
    },
  ]);

  return rows;
}

/** Firma fija al pie de la última página (independiente del alto del contenido). */
function drawPtcSignatureAtPageBottom(doc, pageW, margin) {
  const pageH = doc.internal.pageSize.getHeight();
  const pageNo = doc.getNumberOfPages();
  doc.setPage(pageNo);

  const sigImgW = 40;
  const sigImgH = 15;
  const sigBlockW = 48;
  const imgY = pageH - PTC_SIGNATURE_ZONE_H;
  const sigCenterX = pageW - margin - sigBlockW / 2;
  const imgX = sigCenterX - sigImgW / 2;

  doc.addImage(firmaPtcImage, 'PNG', imgX, imgY, sigImgW, sigImgH);
  const lineY = imgY + sigImgH + 2;
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.2);
  doc.line(pageW - margin - sigBlockW + 2, lineY, pageW - margin - 2, lineY);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.5);
  doc.text('SIGNATURE', sigCenterX, lineY + 3.2, { align: 'center' });
  doc.setFontSize(7);
  doc.text('p.p. PALM TRADE CHILE SPA', sigCenterX, lineY + 6.8, { align: 'center' });
}

/**
 * Construye el documento jsPDF Invoice PTC (sin descargar).
 */
export function buildInvoicePtcPdfDoc(params) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 10;
  const contentW = pageW - 2 * margin;
  /** Espacio reservado al pie para que tablas no tapen la firma fija. */
  const tableMargin = { left: margin, right: margin, bottom: PTC_SIGNATURE_ZONE_H };
  const logoY = 0;
  const boxY = 6;
  let y = boxY;

  const logoW = 34;
  const imgProps = doc.getImageProperties(ptcImage);
  const logoH = (imgProps.height * logoW) / imgProps.width;
  doc.addImage(ptcImage, 'JPEG', margin, logoY, logoW, logoH);

  const infoX = margin + 2;
  let infoY = logoY + logoH + 2;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  COMPANY_LINES.forEach((ln) => {
    doc.text(ln, infoX, infoY);
    infoY += 3.2;
  });

  const boxW = 60;
  const boxX = pageW - margin - boxW;
  autoTable(doc, {
    startY: boxY,
    margin: { left: boxX, right: margin },
    tableWidth: boxW,
    theme: 'plain',
    styles: {
      fontSize: 7,
      cellPadding: { top: 1.4, right: 0.0, bottom: 1.4, left: 0.0 },
      lineColor: [0, 0, 0],
      lineWidth: 0,
      textColor: [0, 0, 0],
      valign: 'middle',
      halign: 'center',
    },
    body: [
      [{ content: `R.U.T: ${RUT_EMPRESA}`, styles: { fontSize: 9, fontStyle: 'bold' } }],
      [
        {
          content: 'FACTURA DE VENTA DE MERCADERIAS\nSITUADAS EN EL EXTRANJERO',
          styles: { fontSize: 7, fontStyle: 'normal' },
        },
      ],
      [
        {
          content: `INVOICE N° ${invoiceNumberCorrelativo(params.numeroInvoice) || ''}`,
          styles: { fontSize: 10, fontStyle: 'bold' },
        },
      ],
    ],
  });

  const yBoxEnd = doc.lastAutoTable.finalY;
  // Solo borde externo del bloque (sin líneas internas).
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.2);
  doc.rect(boxX, boxY, boxW, yBoxEnd - boxY);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.text(SII_OFICINA, boxX + boxW / 2, yBoxEnd + 3.8, { align: 'center' });

  y = Math.max(infoY + 1, yBoxEnd + 5.2);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text(
    `ISSUE DATE: ${params.issueCity || 'Santiago'}, ${invoiceIssueDateText(params.fechaInvoice, fmtDateLongEn)}`,
    margin,
    y + 3.5
  );
  y += 6;

  const leftW = contentW * 0.62;
  const rightW = contentW - leftW;
  const leftLabelW = leftW * 0.37;
  const addrLines = addressLinesWithoutCountry(params.clienteDireccion, params.paisDestino);
  const cityCountry = upper(params.paisDestino);
  const postal = safe(params.pinCode).trim();
  const blockStartY = y;
  const leftEndY = drawLeftCustomerBlock(doc, {
    startX: margin,
    startY: blockStartY,
    blockW: leftW,
    labelW: leftLabelW,
    nombre: upper(params.clienteNombre),
    addrLines,
    cityCountry,
    postal,
  });
  const blockH = leftEndY - blockStartY;
  const rightX = margin + leftW;

  const rightTable = drawRightLogisticsBlock(doc, {
    startY: blockStartY,
    x: rightX,
    width: rightW,
    params,
    blockHeight: blockH,
    closeBottomAt: leftEndY,
  });
  const blockEndY = Math.max(leftEndY, rightTable.finalY);
  sealRightLogisticsBottom(doc, rightTable, blockEndY);
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.15);
  doc.rect(margin, blockStartY, leftW, blockEndY - blockStartY);
  y = blockEndY;

  const labelCell = { fontStyle: 'bold', halign: 'center', valign: 'middle' };
  const netFmt = fmtNum(params.netWeight, 3);
  const grossFmt = fmtNum(params.grossWeight, 3);
  const paymentTxt = safe(params.paymentTerms);

  autoTable(doc, {
    startY: y,
    margin: tableMargin,
    tableWidth: contentW,
    ...tableGrid,
    body: [
      [
        { content: 'Vessel', styles: labelCell },
        { content: 'Contract No.', styles: labelCell },
        { content: 'Purchase Order', styles: labelCell },
        { content: 'Bill of Lading', styles: labelCell },
      ],
      [
        { content: upper(params.nombreNave) },
        { content: upper(params.contractNo) },
        { content: upper(params.purchaseOrder) },
        { content: upper(params.nombreBl) },
      ],
    ],
    columnStyles: col4Vessel(contentW),
  });
  y = doc.lastAutoTable.finalY;

  autoTable(doc, {
    startY: y,
    margin: tableMargin,
    tableWidth: contentW,
    ...tableGrid,
    body: [
      [
        { content: 'Sales Terms', styles: labelCell },
        { content: 'Incoterms', styles: labelCell },
        { content: 'Payment Terms', styles: labelCell },
        {
          content: 'TOTAL WEIGHT (kg)',
          colSpan: 2,
          styles: labelCell,
        },
      ],
      [
        {
          content: upper(params.salesTerms),
          rowSpan: 2,
          styles: { valign: 'middle' },
        },
        {
          content: upper(params.incoterms),
          rowSpan: 2,
          styles: { valign: 'middle' },
        },
        {
          content: paymentTxt,
          rowSpan: 2,
          styles: { halign: 'center', valign: 'middle', fontSize: 6.5 },
        },
        { content: 'Net Weight', styles: labelCell },
        { content: 'Gross Weight', styles: labelCell },
      ],
      [
        { content: netFmt, styles: { valign: 'middle' } },
        { content: grossFmt, styles: { valign: 'middle' } },
      ],
    ],
    columnStyles: col5SalesWeight(contentW),
  });
  y = doc.lastAutoTable.finalY;

  autoTable(doc, {
    startY: y,
    margin: tableMargin,
    tableWidth: contentW,
    ...tableGrid,
    body: [
      [
        {
          content: 'Please Remit Payment to PALM TRADE CHILE SPA',
          colSpan: 5,
          styles: { fontStyle: 'bold', halign: 'center', fontSize: 7.5, valign: 'middle' },
        },
      ],
    ],
    columnStyles: col5SalesWeight(contentW),
  });
  y = doc.lastAutoTable.finalY;

  const lineas = params.lineas || [];
  const bodyMerchRows = buildMerchandiseTableBody(lineas, params.textosAlternativosPtc);

  const totalUsdNum = params.valorFinalUsd != null ? Number(params.valorFinalUsd) : params.valorUsd != null ? Number(params.valorUsd) : null;
  const merchandiseUsdNum = params.valorMercaderiaUsd != null ? Number(params.valorMercaderiaUsd) : totalUsdNum;
  const moneda = safe(params.currency || 'USD').toUpperCase();
  const dueWords = totalUsdNum != null ? totalDueWordsPtc(totalUsdNum, moneda) : '';
  const dueText = dueWords ? `TOTAL DUE: ${dueWords}` : 'TOTAL DUE: ';
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  const dueLines = doc.splitTextToSize(dueText, contentW - 4);
  const dueFooterH = Math.max(14, 6 + dueLines.length * 3.4 + 5);
  const ajustes = Array.isArray(params.ajustesPtc) ? params.ajustesPtc : [];
  const composicion = [
    ['Valor FOB', params.valorFobPtc],
    ['Valor Flete', params.valorFletePtc],
    ['Valor Seguro', params.valorSeguroPtc],
  ].filter(([, value]) => value != null && value !== '' && Number.isFinite(Number(value)));

  const totalBundles = params.totalBundles;
  const totalM3 = params.totalM3;
  const totalUsdFmt = merchandiseUsdNum != null ? fmtNum(merchandiseUsdNum, 2) : '';
  const totalesIzq = totalVolumeSummaryText(
    totalBundles,
    totalM3,
    params.unidadVolumenPtc,
    fmtNum
  );
  const footerRows = buildPtcMerchFooterRows({
    totalesIzq,
    totalUsdFmt,
    moneda,
    ajustes,
    composicion,
    totalUsdNum,
    dueFooterH,
  });
  let footerH;
  let compact = false;
  const dueRowIndex = footerRows.length - 1;
  const merchHead = [
    [
      { content: 'Internal Code', rowSpan: 2, styles: merchHeadLabel },
      { content: 'Quantity', rowSpan: 2, styles: merchHeadLabel },
      {
        content: 'MERCHANDISE DESCRIPTION',
        rowSpan: 2,
        styles: merchHeadCaps,
      },
      { content: 'DJC', colSpan: 5, styles: merchHeadDjcGroup },
    ],
    [
      { content: 'N°', styles: merchHeadLabel },
      { content: 'Date', styles: merchHeadLabel },
      { content: 'Currency', styles: merchHeadLabel },
      { content: 'UNIT PRICE', styles: merchHeadCaps },
      { content: 'TOTAL VALUE', styles: merchHeadCaps },
    ],
  ];
  const merchTableCommon = {
    margin: tableMargin,
    tableWidth: contentW,
    ...tableGrid,
    columnStyles: col8Merchandise(contentW),
  };
  const merchPageLayout = {
    minPage: Number.POSITIVE_INFINITY,
    topYByPage: new Map(),
    bottomYByPage: new Map(),
    headBottomYByPage: new Map(),
  };

  const trackMerchTablePageBounds = (data) => {
    const page = doc.internal.getCurrentPageInfo().pageNumber;
    if (page < merchPageLayout.minPage) merchPageLayout.minPage = page;
    const cellTop = data.cell.y;
    const cellBottom = data.cell.y + data.cell.height;
    const prevTop = merchPageLayout.topYByPage.get(page);
    if (prevTop == null || cellTop < prevTop) merchPageLayout.topYByPage.set(page, cellTop);
    const prevBottom = merchPageLayout.bottomYByPage.get(page) || 0;
    if (cellBottom > prevBottom) merchPageLayout.bottomYByPage.set(page, cellBottom);
    if (data.section === 'head') {
      const prevHeadBottom = merchPageLayout.headBottomYByPage.get(page) || 0;
      if (cellBottom > prevHeadBottom) merchPageLayout.headBottomYByPage.set(page, cellBottom);
    }
  };

  const parseMerchHeadCell = (data) => {
    if (data.section !== 'head') return;
    data.cell.styles.lineColor = merchLineColor;
    if (data.row.index === 0 && data.column.index === 3) {
      data.cell.styles.lineWidth = { ...merchHeadDjcGroup.lineWidth };
    } else {
      data.cell.styles.lineWidth = { ...merchGridLine };
    }
  };

  const parseFooterCell = (data) => {
    if (data.section !== 'body') return;
    data.cell.styles.lineColor = merchLineColor;
    data.cell.styles.lineWidth = { ...merchGridLine };
    if (compact) {
      data.cell.styles.cellPadding = { top: 0.8, right: 1.2, bottom: 0.8, left: 1.2 };
      if (data.row.index === dueRowIndex) {
        data.cell.styles.minCellHeight = Math.max(12, dueFooterH - 2);
      }
    }
  };
  const footerOptions = {
    ...merchTableCommon,
    showHead: false,
    body: footerRows,
    pageBreak: 'avoid',
    rowPageBreak: 'avoid',
    bodyStyles: { valign: 'top', halign: 'center' },
    didParseCell: parseFooterCell,
  };
  const merchandiseOptions = {
    ...merchTableCommon,
    showHead: 'everyPage',
    rowPageBreak: 'avoid',
    head: merchHead,
    body: bodyMerchRows,
    bodyStyles: {
      valign: 'top',
      halign: 'center',
      lineWidth: merchBodyVertLines,
      lineColor: merchLineColor,
    },
    didParseCell(data) {
      parseMerchHeadCell(data);
      if (data.section === 'body') {
        const kind = merchRowKind(data.row.raw);
        if (kind === 'product' || kind === 'texto') {
          data.cell.styles.rowPageBreak = 'avoid';
        }
        applyMerchTableCellBorders(data, kind);
        if (compact) {
          const padding = data.cell.styles.cellPadding;
          data.cell.styles.cellPadding = {
            ...padding,
            top: 0.8,
            bottom: 0.8,
          };
        }
      }
    },
    didDrawCell(data) {
      if (data.section === 'head' || data.section === 'body') {
        trackMerchTablePageBounds(data);
      }
      if (data.section === 'body') {
        drawMerchBodyVerticalOverlap(data);
      }
    },
  };

  const bottomY = ptcSignatureZoneTop(doc) - PTC_FILLER_SAFETY_MM;
  let measured = measurePtcTable(doc, { ...merchandiseOptions, startY: y });
  footerH = measurePtcTable(doc, footerOptions).rows.reduce((sum, height) => sum + height, 0);
  const fitsFirstPage = () =>
    y + measured.head + measured.rows.reduce((sum, height) => sum + height, 0) + footerH <= bottomY;
  if (!fitsFirstPage()) {
    const regularMeasured = measured;
    const regularFooterH = footerH;
    compact = true;
    measured = measurePtcTable(doc, { ...merchandiseOptions, startY: y });
    footerH = measurePtcTable(doc, footerOptions).rows.reduce((sum, height) => sum + height, 0);
    // Compactar únicamente si permite que toda la factura quepa en la primera hoja.
    if (!fitsFirstPage()) {
      compact = false;
      measured = regularMeasured;
      footerH = regularFooterH;
    }
  }
  const pages = planPtcMerchPages(
    bodyMerchRows, measured.rows, measured.head, y, measured.top, bottomY, footerH
  );
  pages.forEach((page, index) => {
    if (index > 0) doc.addPage();
    if (!page.indices.length) return;
    autoTable(doc, {
      ...merchandiseOptions,
      startY: page.startY,
      body: page.indices.map((rowIndex) => bodyMerchRows[rowIndex]),
    });
  });

  const productsEndPage = doc.internal.getCurrentPageInfo().pageNumber;
  const merchStartPage = Number.isFinite(merchPageLayout.minPage)
    ? merchPageLayout.minPage
    : productsEndPage;

  for (let pageNo = merchStartPage; pageNo < productsEndPage; pageNo += 1) {
    doc.setPage(pageNo);
    const bottomY = merchPageLayout.bottomYByPage.get(pageNo);
    if (bottomY == null) continue;
    drawPtcMerchPageClose(
      doc,
      margin,
      contentW,
      bottomY,
      merchPageLayout.headBottomYByPage.get(pageNo)
    );
  }

  doc.setPage(productsEndPage);
  const productsEndY = doc.lastAutoTable.finalY;
  let footerStartY = ptcMerchFooterStartY(doc, productsEndY, footerH);
  if (footerStartY > productsEndY + 3) {
    const fillerH = footerStartY - productsEndY;
    autoTable(doc, {
      startY: productsEndY,
      showHead: false,
      body: [merchFillerRowCells(fillerH)],
      bodyStyles: {
        valign: 'top',
        halign: 'center',
        lineWidth: merchBodyVertLines,
        lineColor: merchLineColor,
      },
      didDrawCell(data) {
        if (data.section === 'body') {
          drawMerchBodyVerticalOverlap(data);
        }
      },
      ...merchTableCommon,
    });
    footerStartY = doc.lastAutoTable.finalY;
  }

  autoTable(doc, {
    ...footerOptions,
    startY: footerStartY,
    didDrawCell(data) {
      if (data.section !== 'body' || data.row.index !== dueRowIndex || data.column.index !== 0) {
        return;
      }
      const { x, y: cellY } = data.cell;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7);
      doc.text(dueLines, x + 1.5, cellY + 4);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6.5);
      doc.text('( IN LETTERS )', x + 1.5, cellY + 4 + dueLines.length * 3.4 + 1);
    },
  });

  drawPtcSignatureAtPageBottom(doc, pageW, margin);
  appendBankInformationPage(doc);

  return doc;
}

export function invoicePtcPdfFileName(params) {
  return `Invoice_PTC_${safe(params.numeroInvoice).replace(/\W/g, '_') || 'preview'}.pdf`;
}

/** Blob URL para vista previa en iframe (revocar con URL.revokeObjectURL al cerrar). */
export function createInvoicePtcPdfBlobUrl(params) {
  const doc = buildInvoicePtcPdfDoc(params);
  const blob = doc.output('blob');
  return URL.createObjectURL(blob);
}

/** Genera y descarga PDF Invoice PTC. */
export function generateInvoicePtcPdf(params) {
  buildInvoicePtcPdfDoc(params).save(invoicePtcPdfFileName(params));
}
